import { useCallback, useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';

/**
 * Owns an ECharts instance for one container.
 *
 * Generalises the init/resize/dispose wiring each src/pages/cpiReports chart
 * repeats inline, and closes a defect those copies share: they resize only on
 * `window.resize`, so a chart first mounted inside a `display: none` container
 * (an inactive tab body) stays at zero width until the window happens to
 * resize. A ResizeObserver on the container fires when the tab becomes
 * visible, which is the event that actually matters here.
 */
export function useEchart(
  option: echarts.EChartsOption | null,
  onItemClick?: (name: string) => void
): (node: HTMLDivElement | null) => void {
  const instance = useRef<echarts.ECharts | null>(null);
  const [node, setNode] = useState<HTMLDivElement | null>(null);

  const ref = useCallback((next: HTMLDivElement | null) => {
    setNode(next);
  }, []);

  // Held in a ref for the same reason useLpiAggregates holds its token getter
  // that way: callers pass an inline closure, which is a new reference every
  // render. Depending on it directly would tear down and rebind the ECharts
  // handler on every unrelated re-render.
  const onItemClickRef = useRef(onItemClick);
  useEffect(() => {
    onItemClickRef.current = onItemClick;
  });

  // Init + apply the option. `notMerge` (true) so a shorter dataset cannot
  // leave stale series behind from a previous filter.
  useEffect(() => {
    if (!node || !option) return;

    if (!instance.current || instance.current.getDom() !== node) {
      instance.current?.dispose();
      instance.current = echarts.init(node);
    }

    instance.current.setOption(option, true);

    // `off` before `on`: this effect re-runs on every option change, and
    // ECharts appends handlers rather than replacing them. Without the off,
    // one click would fire the drill-through once per filter change since
    // mount.
    instance.current.off('click');
    instance.current.on('click', (params: any) => {
      const name = params?.name;
      if (typeof name === 'string') onItemClickRef.current?.(name);
    });
  }, [node, option]);

  useEffect(() => {
    if (!node) return undefined;

    const handleResize = () => instance.current?.resize();
    window.addEventListener('resize', handleResize);

    const observer = new ResizeObserver(handleResize);
    observer.observe(node);

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [node]);

  useEffect(
    () => () => {
      instance.current?.dispose();
      instance.current = null;
    },
    []
  );

  return ref;
}
